export const CHECKOUT_DRAFT_STORAGE_KEY = 'sg.checkout.draft.v1'

export type CheckoutPaymentMethod = 'card' | 'bank-transfer'

export type CheckoutDraft = {
  version: 1
  fullName: string
  phone: string
  email: string
  address: string
  suburb: string
  postcode: string
  deliveryDay: string
  deliveryNotes: string
  discountCode: string
  paymentMethod: CheckoutPaymentMethod
  updatedAt: string
}

export type CheckoutDraftValues = Omit<CheckoutDraft, 'version' | 'updatedAt'>

// Checkout drafts, including an entered discount code, are intentionally session-scoped only.
function storage(): Storage | null {
  if (typeof window === 'undefined') return null
  try {
    return window.sessionStorage
  } catch {
    return null
  }
}

function removeLegacyPersistentDraft(): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.removeItem(CHECKOUT_DRAFT_STORAGE_KEY)
  } catch {
    // Legacy localStorage cleanup is best-effort if browser storage is unavailable.
  }
}

function blankDraft(defaultDeliveryDay: string, defaultPaymentMethod: CheckoutPaymentMethod): CheckoutDraftValues {
  return {
    fullName: '',
    phone: '',
    email: '',
    address: '',
    suburb: '',
    postcode: '',
    deliveryDay: defaultDeliveryDay,
    deliveryNotes: '',
    discountCode: '',
    paymentMethod: defaultPaymentMethod,
  }
}

export function readCheckoutDraft({
  validDeliveryDays,
  validPaymentMethods,
  defaultDeliveryDay,
  defaultPaymentMethod,
}: {
  validDeliveryDays: readonly string[]
  validPaymentMethods: readonly CheckoutPaymentMethod[]
  defaultDeliveryDay: string
  defaultPaymentMethod: CheckoutPaymentMethod
}): CheckoutDraftValues {
  removeLegacyPersistentDraft()

  const fallback = blankDraft(defaultDeliveryDay, defaultPaymentMethod)
  const store = storage()
  if (!store) return fallback

  try {
    const parsed = JSON.parse(store.getItem(CHECKOUT_DRAFT_STORAGE_KEY) ?? '') as Partial<CheckoutDraft>
    if (parsed.version !== 1) return fallback

    const deliveryDay = typeof parsed.deliveryDay === 'string' && validDeliveryDays.includes(parsed.deliveryDay)
      ? parsed.deliveryDay
      : defaultDeliveryDay
    const paymentMethod = (parsed.paymentMethod === 'card' || parsed.paymentMethod === 'bank-transfer')
      && validPaymentMethods.includes(parsed.paymentMethod)
      ? parsed.paymentMethod
      : defaultPaymentMethod

    return {
      fullName: typeof parsed.fullName === 'string' ? parsed.fullName : '',
      phone: typeof parsed.phone === 'string' ? parsed.phone : '',
      email: typeof parsed.email === 'string' ? parsed.email : '',
      address: typeof parsed.address === 'string' ? parsed.address : '',
      suburb: typeof parsed.suburb === 'string' ? parsed.suburb : '',
      postcode: typeof parsed.postcode === 'string' ? parsed.postcode : '',
      deliveryDay,
      deliveryNotes: typeof parsed.deliveryNotes === 'string' ? parsed.deliveryNotes : '',
      discountCode: typeof parsed.discountCode === 'string' ? parsed.discountCode : '',
      paymentMethod,
    }
  } catch {
    return fallback
  }
}

// A pre-existing saved card selection is authoritative, even when Volley later becomes available.
// This must be read on initial checkout entry, before the normal draft autosave writes its default.
export function hasSavedCheckoutPaymentMethod(): boolean {
  const store = storage()
  if (!store) return false
  try {
    const parsed = JSON.parse(store.getItem(CHECKOUT_DRAFT_STORAGE_KEY) ?? '') as Partial<CheckoutDraft>
    return parsed.version === 1 && (parsed.paymentMethod === 'card' || parsed.paymentMethod === 'bank-transfer')
  } catch {
    return false
  }
}

export function shouldPreferVolleyOnCheckout(
  mode: 'legacy' | 'volley' | 'disabled',
  hadSavedPaymentChoice: boolean,
  chosePaymentMethodThisVisit: boolean,
): boolean {
  return mode === 'volley' && !hadSavedPaymentChoice && !chosePaymentMethodThisVisit
}

export function saveCheckoutDraft(values: CheckoutDraftValues): void {
  removeLegacyPersistentDraft()

  const store = storage()
  if (!store) return

  const payload: CheckoutDraft = {
    version: 1,
    ...values,
    updatedAt: new Date().toISOString(),
  }

  try {
    store.setItem(CHECKOUT_DRAFT_STORAGE_KEY, JSON.stringify(payload))
  } catch {
    // Checkout remains usable if browser session storage is unavailable.
  }
}

export function clearCheckoutDraft(): void {
  const store = storage()
  if (store) {
    try {
      store.removeItem(CHECKOUT_DRAFT_STORAGE_KEY)
    } catch {
      // Explicit clearing is best-effort if browser session storage is unavailable.
    }
  }

  removeLegacyPersistentDraft()
}
